/**
 * Public Threat Radar — `/threat-radar`
 *
 * Marketing weapon: live 3D globe of public threat intelligence + the
 * Q-Day countdown + qubit counter + NIST PQC tracker + HNDL counter.
 * Open to the world. SEO-rich. Demo-clip energy.
 *
 * Backend feeds (no auth):
 *   GET /api/public/threat-radar/feed   — points + stats + q_day + qubits + nist_pqc
 *   GET /api/public/threat-radar/news   — latest CVE/KEV/exploits
 *   GET /api/public/threat-radar/hndl   — live BTC+ETH block heights
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import ThreatGlobe from "../components/admin/ciso/ThreatGlobe";
import useDocumentTitle from "../hooks/useDocumentTitle";
import usePageMeta from "../hooks/usePageMeta";
import {
  Cpu, Shield, AlertTriangle, ExternalLink, ArrowRight,
  Newspaper, Atom, Radio, FileWarning, Code, X,
  Copy, Check, Ticket,
  Twitter, Linkedin, Share2,
  ShieldCheck, MapPin, Building2, Calendar, Search,
  MessageCircle, Filter,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS_FEED = 60_000;     // news every 60s
const POLL_MS_HNDL = 12_000;     // chain heights every 12s

const FILTER_GROUPS = [
  { key: "kev",     color: "#FF2A2A", icon: AlertTriangle, label: "Actively Exploited",
    match: (p) => p.kind === "news" && p.category === "kev" },
  { key: "cve",     color: "#FF8000", icon: FileWarning,   label: "CVE / NVD",
    match: (p) => p.kind === "news" && p.category === "cve" },
  { key: "ghsa",    color: "#FFD60A", icon: Code,          label: "Supply-Chain (GHSA)",
    match: (p) => p.kind === "news" && p.category === "vendor" },
  { key: "news",    color: "#1FDD83", icon: Newspaper,     label: "Cyber News",
    match: (p) => p.kind === "news" && p.category === "news" },
  { key: "qcompute", color: "#9B7CFF", icon: Atom,         label: "Quantum Compute",
    match: (p) => p.kind === "quantum" && (p.type || "compute") === "compute" },
  { key: "qdefense", color: "#00E5FF", icon: ShieldCheck,  label: "Quantum Defense",
    match: (p) => p.kind === "quantum" && p.type === "defense" },
];

export default function ThreatRadar() {
  useDocumentTitle("Threat Radar — Live Quantum & Web3 Security Intelligence | QorTrace");
  usePageMeta({
    title: "Threat Radar — Live Quantum & Web3 Security Intelligence | QorTrace",
    description: "Live globe of post-quantum cryptographic threats — Q-Day countdown, KEV/CVE pulses, on-chain exposure, treasury at-risk. Updated continuously by QorTrace.",
    canonicalPath: "/threat-radar",
  });

  const [feed,    setFeed]    = useState(null);
  const [news,    setNews]    = useState([]);
  const [hndl,    setHndl]    = useState(null);
  const [selected, setSelected] = useState(null);
  const [flyTo,   setFlyTo]   = useState(null);
  const [region,  setRegion]  = useState(null);   // {lat, lon, count, categories, country}
  const [visible, setVisible] = useState(() => new Set(FILTER_GROUPS.map((g) => g.key)));
  const toggleFilter = (k) => setVisible((s) => {
    const next = new Set(s);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  // Globe points are filtered purely by the 5 category chips.
  const filteredPoints = React.useMemo(() => {
    if (!feed?.points) return [];
    return feed.points.filter(
      (p) => FILTER_GROUPS.some((g) => visible.has(g.key) && g.match(p)),
    );
  }, [feed, visible]);

  const refreshFeed = useCallback(async () => {
    try {
      const [f, n] = await Promise.all([
        axios.get(`${API}/api/public/threat-radar/feed?hours=336`),
        axios.get(`${API}/api/public/threat-radar/news?limit=40`),
      ]);
      setFeed(f.data);
      setNews(n.data?.news || []);
    } catch (_e) { /* network blip — keep last data */ }
  }, []);

  const refreshHndl = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/public/threat-radar/hndl`);
      setHndl(r.data);
    } catch (_e) { /* keep last */ }
  }, []);

  useEffect(() => {
    refreshFeed();
    refreshHndl();
    const tF = setInterval(refreshFeed, POLL_MS_FEED);
    const tH = setInterval(refreshHndl, POLL_MS_HNDL);
    return () => { clearInterval(tF); clearInterval(tH); };
  }, [refreshFeed, refreshHndl]);

  // One-shot visitor flyover. We wait briefly so the camera's intro
  // settle (1500ms tween in ThreatGlobe) can complete before we start
  // the second tween. Skipped if `?inspect=` deep-link is present —
  // those visitors want to see the advisory, not their country.
  // If `?lat=&lon=` are explicitly supplied (e.g. via /r/<code>), use
  // them directly and skip the network geo-lookup entirely.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("inspect")) return;

    const qLat = parseFloat(params.get("lat"));
    const qLon = parseFloat(params.get("lon"));
    if (Number.isFinite(qLat) && Number.isFinite(qLon)
        && Math.abs(qLat) <= 90 && Math.abs(qLon) <= 180) {
      // Pre-supplied coordinates — no fetch needed.
      const t = setTimeout(() => {
        setFlyTo({ lat: qLat, lon: qLon,
                   country: (params.get("country") || "").toUpperCase() || null });
      }, 1700);
      return () => clearTimeout(t);
    }

    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/public/threat-radar/visitor-geo`,
                                  { timeout: 5000 });
        if (cancelled) return;
        const { lat, lon } = r.data || {};
        if (typeof lat === "number" && typeof lon === "number") {
          setFlyTo({ lat, lon, country: r.data?.country || null });
        }
      } catch (_e) { /* silent — globe stays on default lat/lng */ }
    }, 1700);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  // After the visitor flyover lands, find the nearest news cluster and
  // surface a tooltip with a "Click to filter" CTA. Auto-dismisses after
  // 12 seconds. Skipped if the user has already opened a drawer.
  useEffect(() => {
    if (!flyTo || !feed?.points?.length) return;
    if (selected) return;                        // user already engaged
    const t = setTimeout(() => {
      const newsPts = feed.points.filter((p) => p.kind === "news"
                                              && p.lat != null && p.lon != null);
      if (!newsPts.length) return;
      // Find nearest news pulse to the visitor
      let nearest = null;
      let nearestDist = Infinity;
      for (const p of newsPts) {
        const d = haversineKm(flyTo.lat, flyTo.lon, p.lat, p.lon);
        if (d < nearestDist) { nearest = p; nearestDist = d; }
      }
      if (!nearest) return;
      // Cluster = everything within 800km of the nearest anchor
      const cluster = newsPts.filter(
        (p) => haversineKm(nearest.lat, nearest.lon, p.lat, p.lon) <= 800,
      );
      const cats = [...new Set(cluster.map((p) => p.category).filter(Boolean))];
      setRegion({
        lat:        nearest.lat,
        lon:        nearest.lon,
        count:      cluster.length,
        categories: cats,
        country:    flyTo.country,
      });
    }, 2400);
    return () => clearTimeout(t);
  }, [flyTo, feed, selected]);

  // Auto-dismiss the region tooltip after 12s
  useEffect(() => {
    if (!region) return;
    const t = setTimeout(() => setRegion(null), 12_000);
    return () => clearTimeout(t);
  }, [region]);

  // Drawer open → kill the tooltip immediately; the user has engaged
  useEffect(() => { if (selected && region) setRegion(null); }, [selected, region]);

  // Deep-link support: `?inspect=<point_id>` (e.g. nvd:CVE-2024-3094)
  // pre-opens the drawer once the feed lands so links shared on socials
  // land directly on the advisory.
  useEffect(() => {
    if (!feed?.points?.length || selected) return;
    const params = new URLSearchParams(window.location.search);
    const id = params.get("inspect");
    if (!id) return;
    const match = feed.points.find((p) => p.id === id);
    if (match) setSelected(match);
    // If the id isn't in the live feed, synthesise a minimal point so the
    // drawer can still fetch detail from /detail/{id}.
    else setSelected({ id, kind: "news", label: id, category: id.startsWith("kev:") ? "kev"
                                                          : id.startsWith("ghsa:") ? "vendor"
                                                          : "cve" });
  }, [feed, selected]);

  // Local Q-Day countdown ticker (smooth seconds — no need to refetch)
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const qDay = feed?.q_day?.median;
  const ogCapture = useMemo(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("og_capture") === "1";
  }, []);
  // Apply a body-scope class when og_capture=1 so siblings like the
  // cookie banner (rendered outside the radar tree) can be hidden too.
  useEffect(() => {
    if (!ogCapture) return;
    document.body.classList.add("tr-og-capture-body");
    return () => { document.body.classList.remove("tr-og-capture-body"); };
  }, [ogCapture]);
  const qDayCounters = useMemo(() => {
    if (!qDay) return null;
    // Backend now returns either an ISO date or full ISO datetime; let
    // the Date constructor parse it directly so we keep h/m/s precision.
    const target = new Date(qDay.target).getTime();
    const remain = Math.max(0, Math.floor((target - now) / 1000));
    const days = Math.floor(remain / 86400);
    const hrs  = Math.floor((remain - days * 86400) / 3600);
    const mins = Math.floor((remain - days * 86400 - hrs * 3600) / 60);
    const secs = remain - days * 86400 - hrs * 3600 - mins * 60;
    return { days, hrs, mins, secs };
  }, [qDay, now]);

  return (
    <div data-testid="threat-radar-page"
         className={ogCapture ? "tr-og-capture" : ""}
         style={{ minHeight: "100vh", background: "#000", color: "#e8eaf2",
                  fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>
      <SEOHead />
      {ogCapture && (
        <style>{`
          /* og_capture=1 — strip ALL UI chrome so the recorded video is
             just the globe + minimal brand stamp + Q-Day clock. Used by
             /api/public/threat-radar/og.mp4 and /og/r/<code>.mp4. */
          .tr-og-capture [data-testid="tr-below-fold"],
          .tr-og-capture nav,
          .tr-og-capture header,
          .tr-og-capture [data-testid="tr-region-tooltip"],
          .tr-og-capture [data-testid="tr-news-ticker"],
          .tr-og-capture [data-testid="tr-scroll-cue"],
          .tr-og-capture [data-testid^="tr-filter-"],
          .tr-og-capture [data-testid="globe-zoom-in"],
          .tr-og-capture [data-testid="globe-zoom-out"],
          .tr-og-capture [data-tr-title-block],
          .tr-og-capture [data-tr-hud-right],
          .tr-og-capture [data-tr-filter-chips] { display: none !important; }
          /* Body-scope rules — hide siblings rendered outside the radar
             tree (cookie banner, app-level toasts, intercom widget, etc.). */
          .tr-og-capture-body [data-testid^="cookie-banner"],
          .tr-og-capture-body [data-cookie-banner],
          .tr-og-capture-body [class*="cookie" i] { display: none !important; }
          .tr-og-capture-body { overflow: hidden !important; }
        `}</style>
      )}

      {/* ============================ HERO ============================ */}
      <section style={{ position: "relative", height: "100vh", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0 }}>
          {feed && (
            <ThreatGlobe
              points={filteredPoints}
              arcs={[]}
              onSelectPoint={setSelected}
              visibleKinds={null}
              warRoom={false}
              drawerOpen={!!selected}
              flyToOnce={flyTo}
              regionHighlight={region ? { lat: region.lat, lon: region.lon } : null}
            />
          )}

          {/* Region tooltip — pops 2.4s after the visitor flyover lands.
              Floats centered above the globe so it doesn't compete with
              filter chips or the news ticker. Click → narrows the
              filter chips to the categories present in this cluster. */}
          {region && !selected && (
            <RegionTooltip
              region={region}
              onFilter={() => {
                const chipKeys = region.categories
                  .map((c) => CATEGORY_TO_CHIP[c])
                  .filter(Boolean);
                if (chipKeys.length > 0) {
                  setVisible(new Set(chipKeys));
                }
                setRegion(null);
              }}
              onClose={() => setRegion(null)}
            />
          )}
          {!feed && (
            <div style={{ position: "absolute", inset: 0, display: "flex",
                          alignItems: "center", justifyContent: "center",
                          color: "rgba(255,255,255,0.4)",
                          fontFamily: "ui-monospace, Menlo, monospace",
                          letterSpacing: "0.18em", fontSize: 12 }}>
              CALIBRATING THREAT MAP…
            </div>
          )}
        </div>
        <div aria-hidden="true"
             style={{ position: "absolute", inset: 0, pointerEvents: "none",
                      background: "radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.7) 100%)" }} />

        {/* Top nav strip */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 6,
                      padding: "20px 32px", display: "flex",
                      justifyContent: "space-between", alignItems: "center",
                      pointerEvents: "auto" }}>
          <Link to="/" data-testid="tr-logo-home"
                style={{ display: "inline-flex", alignItems: "center" }}>
            <img src="/qortrace-logo.png"
                 alt="QorTrace"
                 style={{ height: 38, width: "auto", display: "block" }} />
          </Link>
          <div style={{ display: "flex", gap: 18, fontSize: 13 }}>
          </div>
        </div>

        {/* Title block */}
        <div data-tr-title-block
             style={{ position: "absolute", top: 80, left: 32, zIndex: 5, maxWidth: 460 }}>
          <div style={{ color: "#00E5FF", fontFamily: "ui-monospace,Menlo,monospace",
                        letterSpacing: "0.18em", fontSize: 11, marginBottom: 6 }}>
            QORTRACE THREAT RADAR · LIVE
          </div>
          <h1 style={{ fontSize: 44, fontWeight: 800, lineHeight: 1.05,
                       color: "#fff", margin: 0, letterSpacing: "-0.02em" }}>
            The clock is<br/>
            <span style={{ color: "#FF2A2A" }}>already</span> ticking.
          </h1>
          <p style={{ marginTop: 14, color: "#9aa5b8", fontSize: 14, maxWidth: 400 }}>
            A live map of post-quantum and Web3 threats. Every signature you commit
            today is harvest-now-decrypt-later candy when fault-tolerant quantum
            arrives. Watch the threat surface — and the countdown — in real time.
          </p>
          <div style={{ marginTop: 16, display: "flex", flexDirection: "column",
                        gap: 10, width: 220 }}>
            <Link to="/audit/talk-to-sales" data-testid="tr-cta-schedule"
                  style={{ background: "#00E5FF", color: "#000", padding: "11px 16px",
                           borderRadius: 3, fontWeight: 700, fontSize: 13,
                           textDecoration: "none", display: "inline-flex", alignItems: "center",
                           justifyContent: "center", gap: 6, width: "100%",
                           boxSizing: "border-box" }}>
              Schedule Consult
            </Link>
          </div>
        </div>

        {/* Q-Day Clock — top center hero */}
        {qDayCounters && (
          <div data-testid="tr-q-day-clock"
               style={{ position: "absolute", top: 80, left: "50%",
                        transform: "translateX(-50%)", zIndex: 5,
                        textAlign: "center" }}>
            <div style={{ fontFamily: "ui-monospace,Menlo,monospace",
                          color: "#FF8000", letterSpacing: "0.22em",
                          fontSize: 10, fontWeight: 600 }}>
              ESTIMATED Q-DAY · {new Date(feed.q_day.median.target)
                .toISOString().slice(0, 10)}
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 14, marginTop: 8 }}>
              {[
                { label: "DAYS", v: qDayCounters.days,  minW: 90 },
                { label: "HRS",  v: qDayCounters.hrs,   minW: 60 },
                { label: "MIN",  v: qDayCounters.mins,  minW: 60 },
                { label: "SEC",  v: qDayCounters.secs,  minW: 60 },
              ].map((c) => (
                <div key={c.label} style={{ textAlign: "center" }}>
                  <div style={{
                    fontFamily: "ui-monospace,Menlo,monospace",
                    fontSize: 38, fontWeight: 700, color: "#fff",
                    background: "rgba(255,42,42,0.12)",
                    border: "1px solid rgba(255,42,42,0.4)",
                    padding: "6px 12px", minWidth: c.minW,
                    textShadow: "0 0 16px rgba(255,42,42,0.6)",
                    letterSpacing: "0.06em",
                  }}>
                    {c.v}
                  </div>
                  <div style={{ fontFamily: "ui-monospace,Menlo,monospace",
                                fontSize: 9, color: "#9aa5b8",
                                letterSpacing: "0.18em", marginTop: 4 }}>
                    {c.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SOTA Qubit + HNDL — top right HUD */}
        <div data-tr-hud-right
             style={{ position: "absolute", top: 32, right: 32, zIndex: 5,
                      display: "flex", flexDirection: "column", gap: 12, width: 280 }}>
          {feed?.qubits && (
            <HudCard title="STATE OF THE ART" icon={Cpu} accent="#9B7CFF">
              <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "baseline" }}>
                <div style={{ fontSize: 30, fontFamily: "ui-monospace,Menlo,monospace",
                              color: "#fff", fontWeight: 700, lineHeight: 1 }}>
                  {feed.qubits.physical.qubits.toLocaleString()}
                </div>
                <div style={{ fontSize: 11, color: "#9aa5b8" }}>physical qubits</div>
              </div>
              <div style={{ fontSize: 11, color: "#9B7CFF", marginTop: 6, fontWeight: 600 }}>
                {feed.qubits.physical.vendor} · {feed.qubits.physical.system}
              </div>
              <div style={{ fontSize: 10, color: "#9aa5b8", marginTop: 2 }}>
                Best logical: {feed.qubits.logical.qubits} q
                ({feed.qubits.logical.vendor})
              </div>
            </HudCard>
          )}
          {hndl && (
            <Link to="/threat-radar/exposed-wallets"
                  data-testid="tr-hndl-card-link"
                  style={{ textDecoration: "none", display: "block" }}>
              <HudCard title="HARVEST · NOW · DECRYPT · LATER" icon={Radio} accent="#FF8000">
                <div style={{ fontSize: 22, fontFamily: "ui-monospace,Menlo,monospace",
                              color: "#fff", fontWeight: 700, lineHeight: 1 }}>
                  {Number(hndl.ecdsa_sigs_estimate || 0).toLocaleString()}
                </div>
                <div style={{ fontSize: 10, color: "#9aa5b8", marginTop: 4,
                              letterSpacing: "0.06em" }}>
                  ECDSA signatures committed onchain (BTC + ETH)
                </div>
                <div style={{ fontSize: 9, color: "#FF8000", marginTop: 6,
                              letterSpacing: "0.1em", fontWeight: 700 }}>
                  BTC #{hndl.btc_block_height ?? "—"} · ETH #{hndl.eth_block_height?.toLocaleString() ?? "—"}
                </div>
                <div style={{ fontSize: 9, color: "#00E5FF", marginTop: 8,
                              letterSpacing: "0.14em", fontWeight: 700 }}>
                  SEE EXPOSED WALLETS →
                </div>
              </HudCard>
            </Link>
          )}
          <BriefingSignup />
          <TweetThisView country={flyTo?.country} />
        </div>

        {/* Filter chips — News/CVE stacked, then a side-by-side Compute/Defense row.
            Sorted by label length ASC so the longest news chip is at the bottom.
            Iter277: Added a "FILTERS · TAP TO TOGGLE" header so users
            immediately recognise these are interactive layer toggles, and
            dimmed the OFF state so hidden layers visually read as muted. */}
        <div data-tr-filter-chips
             style={{ position: "absolute", bottom: 76, left: 32, zIndex: 5,
                      display: "flex", flexDirection: "column",
                      alignItems: "flex-start", gap: 6 }}>
          <div data-testid="tr-filter-header"
               style={{ fontFamily: "ui-monospace,Menlo,monospace",
                        fontSize: 10, letterSpacing: "0.18em", fontWeight: 700,
                        color: "#9aa5b8", marginBottom: 4,
                        display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Filter size={11} style={{ color: "#00E5FF" }} />
            FILTERS · TAP TO TOGGLE LAYERS
          </div>
          {[...FILTER_GROUPS]
            .filter((g) => g.key !== "qcompute" && g.key !== "qdefense")
            .sort((a, b) => a.label.length - b.label.length)
            .map((g) => {
              const Ic = g.icon;
              const on = visible.has(g.key);
              const n = (feed?.points || []).filter(g.match).length;
              return (
                <button key={g.key}
                        data-testid={`tr-filter-${g.key}`}
                        onClick={() => toggleFilter(g.key)}
                        title={on ? `Hide ${g.label} layer` : `Show ${g.label} layer`}
                        aria-pressed={on}
                        style={{
                          background: on ? `${g.color}18` : "rgba(0,0,0,0.55)",
                          border: `1px ${on ? "solid" : "dashed"} ${on ? g.color : "rgba(255,255,255,0.25)"}`,
                          color: on ? g.color : "#6c7787",
                          opacity: on ? 1 : 0.65,
                          padding: "6px 12px", fontSize: 11,
                          fontFamily: "ui-monospace,Menlo,monospace",
                          letterSpacing: "0.1em", borderRadius: 3,
                          textTransform: "uppercase", cursor: "pointer",
                          display: "inline-flex", alignItems: "center", gap: 6,
                          width: "fit-content",
                          transition: "opacity 0.15s, border-color 0.15s, color 0.15s",
                        }}>
                  <Ic size={11} /> {g.label}
                  <span style={{ background: on ? `${g.color}30` : "rgba(255,255,255,0.06)",
                                 padding: "1px 5px", borderRadius: 2,
                                 fontSize: 9, fontWeight: 700 }}>{n}</span>
                </button>
              );
            })}

          {/* Quantum Compute / Quantum Defense — paired row with `/` separator */}
          <div style={{ display: "flex", alignItems: "stretch", gap: 0,
                        width: "fit-content" }}>
            {["qcompute", "qdefense"].map((k, idx) => {
              const g = FILTER_GROUPS.find((x) => x.key === k);
              const Ic = g.icon;
              const on = visible.has(g.key);
              const n  = (feed?.points || []).filter(g.match).length;
              return (
                <React.Fragment key={k}>
                  {idx === 1 && (
                    <span aria-hidden="true"
                          style={{ display: "inline-flex", alignItems: "center",
                                   color: "rgba(255,255,255,0.30)", padding: "0 6px",
                                   fontFamily: "ui-monospace,Menlo,monospace",
                                   fontSize: 14, userSelect: "none" }}>
                      /
                    </span>
                  )}
                  <button data-testid={`tr-filter-${g.key}`}
                          onClick={() => toggleFilter(g.key)}
                          title={on ? `Hide ${g.label} layer` : `Show ${g.label} layer`}
                          aria-pressed={on}
                          style={{
                            background: on ? `${g.color}18` : "rgba(0,0,0,0.55)",
                            border: `1px ${on ? "solid" : "dashed"} ${on ? g.color : "rgba(255,255,255,0.25)"}`,
                            color: on ? g.color : "#6c7787",
                            opacity: on ? 1 : 0.65,
                            padding: "6px 12px", fontSize: 11,
                            fontFamily: "ui-monospace,Menlo,monospace",
                            letterSpacing: "0.1em", borderRadius: 3,
                            textTransform: "uppercase", cursor: "pointer",
                            display: "inline-flex", alignItems: "center", gap: 6,
                            transition: "opacity 0.15s, border-color 0.15s, color 0.15s",
                          }}>
                    <Ic size={11} /> {g.label}
                    <span style={{ background: on ? `${g.color}30` : "rgba(255,255,255,0.06)",
                                   padding: "1px 5px", borderRadius: 2,
                                   fontSize: 9, fontWeight: 700 }}>{n}</span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Bottom news ticker */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 5,
                      background: "linear-gradient(to top, rgba(0,0,0,0.95), rgba(0,0,0,0.4))",
                      borderTop: "1px solid rgba(255,255,255,0.10)",
                      padding: "12px 32px", overflow: "hidden" }}>
          <NewsTicker news={news.slice(0, 16)} />
        </div>

        {/* Scroll-for-more affordance — sits just above the news ticker.
            Hidden once the drawer is open so it doesn't compete for attention. */}
        {!selected && (
          <button
            data-testid="tr-scroll-cue"
            onClick={() => {
              const target = document.querySelector('[data-testid="tr-below-fold"]');
              if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            aria-label="Scroll to live briefing"
            style={{ position: "absolute", bottom: 76, left: "50%",
                     transform: "translateX(-50%)", zIndex: 6,
                     background: "rgba(4,9,20,0.78)",
                     border: "1px solid rgba(0,229,255,0.35)",
                     color: "#00E5FF", padding: "10px 18px",
                     borderRadius: 999, cursor: "pointer",
                     fontFamily: "ui-monospace,Menlo,monospace",
                     fontSize: 11, letterSpacing: "0.18em", fontWeight: 700,
                     backdropFilter: "blur(8px)",
                     animation: "qortrace-bob 2.4s ease-in-out infinite" }}>
            ↓ CONTINUE BELOW · LIVE BRIEFING
          </button>
        )}
        <style>{`
          @keyframes qortrace-bob {
            0%,100% { transform: translateX(-50%) translateY(0); opacity: .85; }
            50%     { transform: translateX(-50%) translateY(6px); opacity: 1; }
          }
        `}</style>

        {selected && (
          <PublicDrawer point={selected} onClose={() => setSelected(null)} />
        )}
      </section>

      {/* ============================ BELOW-FOLD ============================ */}
      <section data-testid="tr-below-fold"
               style={{ padding: "80px 32px", maxWidth: 1280, margin: "0 auto" }}>
        <SectionGrid feed={feed} news={news} />
      </section>

      {/* ============================ CTA STRIP ============================ */}
      <section style={{ padding: "60px 32px", borderTop: "1px solid rgba(255,255,255,0.1)",
                        textAlign: "center" }}>
        <h2 style={{ fontSize: 32, fontWeight: 800, color: "#fff", margin: 0 }}>
          Don't wait for Q-Day.
        </h2>
        <p style={{ color: "#9aa5b8", maxWidth: 540, margin: "12px auto 0" }}>
          QorTrace audits smart contracts, scans wallets for cryptographic exposure,
          and certifies post-quantum readiness. The strongest hands move first.
        </p>
        <div style={{ marginTop: 22, display: "flex", justifyContent: "center", gap: 10 }}>
          <Link to="/audit"
                style={{ background: "#00E5FF", color: "#000", padding: "12px 20px",
                         borderRadius: 3, fontWeight: 700, textDecoration: "none" }}>
            Get audited
          </Link>
          <Link to="/pricing"
                style={{ background: "transparent", color: "#fff",
                         border: "1px solid rgba(255,255,255,0.25)",
                         padding: "12px 20px", borderRadius: 3, fontWeight: 600,
                         textDecoration: "none" }}>
            See pricing
          </Link>
        </div>
      </section>
    </div>
  );
}


// ============================================================================
// Subcomponents
// ============================================================================

function HudCard({ title, icon: Icon, accent, children }) {
  return (
    <div style={{
      background: "rgba(8,12,22,0.78)",
      backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
      border: "1px solid rgba(255,255,255,0.10)",
      borderLeft: `2px solid ${accent}`,
      borderRadius: 3, padding: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6,
                    color: accent, fontFamily: "ui-monospace,Menlo,monospace",
                    letterSpacing: "0.16em", fontSize: 9, fontWeight: 700,
                    marginBottom: 10 }}>
        {Icon && <Icon size={11} />} {title}
      </div>
      {children}
    </div>
  );
}

function NewsTicker({ news }) {
  if (!news?.length) {
    return (
      <div style={{ color: "#6b7280", fontFamily: "ui-monospace,Menlo,monospace",
                    fontSize: 11, letterSpacing: "0.12em" }}>
        FEED · LISTENING — first ingest cycle in progress
      </div>
    );
  }
  return (
    <div data-testid="tr-news-ticker"
         style={{ display: "flex", gap: 28, whiteSpace: "nowrap", overflowX: "auto",
                  scrollbarWidth: "none" }}>
      {news.map((n, i) => {
        const c = n.severity === "critical" ? "#FF2A2A"
                : n.severity === "high"     ? "#FF8000"
                : n.severity === "medium"   ? "#FFD60A"
                : "#9aa5b8";
        return (
          <a key={i} href={n.url} target="_blank" rel="noopener noreferrer"
             style={{ display: "flex", gap: 8, alignItems: "center",
                      fontFamily: "ui-monospace,Menlo,monospace",
                      fontSize: 11, color: c, textDecoration: "none",
                      letterSpacing: "0.04em" }}>
            <span style={{ color: c, fontWeight: 700, letterSpacing: "0.12em" }}>
              {(n.source || "").toUpperCase()}
            </span>
            <span style={{ color: "#cdd3e1" }}>{n.title}</span>
            <ExternalLink size={10} style={{ opacity: 0.6 }} />
          </a>
        );
      })}
    </div>
  );
}

function RegionTooltip({ region, onFilter, onClose }) {
  const place = region.country
    ? region.country.length === 2
      ? `your region (${region.country})`
      : `your region · ${region.country}`
    : "your region";
  return (
    <div data-testid="tr-region-tooltip"
         style={{ position: "absolute", top: 90, left: "50%",
                  transform: "translateX(-50%)", zIndex: 7,
                  background: "rgba(4,9,20,0.96)",
                  border: "1px solid #00E5FF",
                  boxShadow: "0 0 28px rgba(0,229,255,0.35)",
                  borderRadius: 6, padding: "12px 14px 14px",
                  display: "flex", alignItems: "stretch", gap: 12,
                  maxWidth: 460, animation: "qt-tt-in 0.45s ease-out" }}>
      <div style={{ flex: 1 }}>
        <div style={{ color: "#00E5FF",
                      fontFamily: "ui-monospace,Menlo,monospace",
                      letterSpacing: "0.18em", fontSize: 10,
                      fontWeight: 700 }}>
          ◉ NEAR YOU
        </div>
        <div style={{ color: "#fff", fontWeight: 700, fontSize: 15,
                      marginTop: 4, lineHeight: 1.35 }}>
          {region.count} active threat{region.count === 1 ? "" : "s"} in {place}
        </div>
        <button onClick={onFilter}
                data-testid="tr-region-filter"
                style={{ marginTop: 10, background: "#00E5FF",
                         color: "#04090e", border: "none",
                         padding: "7px 12px", borderRadius: 3,
                         cursor: "pointer",
                         fontFamily: "ui-monospace,Menlo,monospace",
                         fontSize: 11, letterSpacing: "0.14em",
                         fontWeight: 800,
                         display: "inline-flex", alignItems: "center", gap: 6 }}>
          CLICK TO FILTER →
        </button>
      </div>
      <button onClick={onClose}
              data-testid="tr-region-close"
              aria-label="Dismiss"
              style={{ background: "transparent", border: "none",
                       color: "#9aa5b8", cursor: "pointer",
                       alignSelf: "flex-start", padding: 0,
                       marginLeft: 4 }}>
        <X size={16} />
      </button>
      <style>{`
        @keyframes qt-tt-in {
          from { opacity: 0; transform: translate(-50%, -8px); }
          to   { opacity: 1; transform: translate(-50%, 0);    }
        }
      `}</style>
    </div>
  );
}

function PublicDrawer({ point, onClose }) {
  // Map any incoming point to the right filter group's color/icon/label
  const meta = (FILTER_GROUPS.find((g) => g.match(point)) || {
    color: "#9aa5b8", icon: null, label: point.kind,
  });
  const Ic = meta.icon;

  // Live-fetch NIST-style rich detail when a news point is selected.
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setDetail(null);
    if (!point.id) return;
    // Fetch live detail for both news AND quantum points.
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/public/threat-radar/detail/${encodeURIComponent(point.id)}`)
      .then((r) => { if (!cancelled) setDetail(r.data); })
      .catch(() => { if (!cancelled) setDetail({ error: true }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [point.id]);

  const isNews    = point.kind === "news";
  const isQuantum = point.kind === "quantum";

  return (
    <div data-testid="tr-detail-drawer"
         style={{ position: "absolute", top: 0, right: 0, bottom: 60, width: 520, zIndex: 8,
                  background: "rgba(4,9,20,0.96)", backdropFilter: "blur(18px)",
                  borderLeft: `2px solid ${meta.color}`,
                  padding: 24, overflowY: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                    marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          {Ic && <Ic size={22} style={{ color: meta.color, marginTop: 2 }} />}
          <div>
            <div style={{ color: meta.color, fontFamily: "ui-monospace,Menlo,monospace",
                          letterSpacing: "0.16em", fontSize: 12, fontWeight: 700 }}>
              {meta.label}
            </div>
            <div style={{ color: "#fff", fontWeight: 700, marginTop: 4,
                          fontSize: 19, lineHeight: 1.3 }}>
              {detail?.cve_id || detail?.ghsa_id || (isQuantum && detail?.title) || point.label}
            </div>
            {detail?.cve_id && detail?.title && detail.title !== detail.cve_id && (
              <div style={{ color: "#cdd3e1", fontSize: 14, marginTop: 6, lineHeight: 1.5 }}>
                {detail.title}
              </div>
            )}
          </div>
        </div>
        <button data-testid="tr-drawer-close" onClick={onClose}
                style={{ background: "transparent", border: "none", color: "#9aa5b8",
                         cursor: "pointer", flexShrink: 0 }}>
          <X size={20} />
        </button>
      </div>

      {/* Quantum facility — rich profile + live press feed */}
      {isQuantum && loading && !detail && <DrawerSpinner label="LOADING FACILITY..." />}
      {isQuantum && detail && !detail.error && (
        <QuantumFacilityDetail detail={detail} accent={meta.color} point={point} />
      )}

      {isNews && loading && !detail && <DrawerSpinner label="FETCHING ADVISORY..." />}

      {/* News dot — split into article-style (category=news) vs CVE/KEV/GHSA NIST view */}
      {isNews && detail && !detail.error && point.category === "news" && (
        <NewsArticleDetail detail={detail} point={point} accent={meta.color} />
      )}
      {isNews && detail && !detail.error && point.category !== "news" && (
        <NvdStyleDetail detail={detail} accent={meta.color} />
      )}

      {isNews && detail?.error && (
        <>
          <Field label="ORIGIN"  value={point.sub} />
          {point.ts && <Field label="PUBLISHED" value={new Date(point.ts).toLocaleString()} mono />}
          <div style={{ color: "#FF8000", fontSize: 13, marginTop: 14,
                        fontFamily: "ui-monospace,Menlo,monospace" }}>
            ⚠ Detail fetch failed — see source link below.
          </div>
        </>
      )}

      {(detail?.nvd_url || point.url) && (
        <a href={detail?.nvd_url || point.url} target="_blank" rel="noopener noreferrer"
           data-testid="tr-drawer-source"
           style={{ marginTop: 22, display: "inline-flex", alignItems: "center", gap: 6,
                    color: meta.color, textDecoration: "none",
                    fontFamily: "ui-monospace,Menlo,monospace", fontSize: 13,
                    letterSpacing: "0.1em", fontWeight: 700 }}>
          ▾ {isQuantum ? "OFFICIAL SITE" : "READ FULL ADVISORY"} <ExternalLink size={14} />
        </a>
      )}

      {/* Drawer footer actions — only meaningful for news pulses */}
      {isNews && <DrawerActions point={point} detail={detail} accent={meta.color} />}
    </div>
  );
}

function DrawerSpinner({ label }) {
  return (
    <div style={{ color: "#9aa5b8", fontFamily: "ui-monospace,Menlo,monospace",
                  fontSize: 12, letterSpacing: "0.14em", padding: "24px 0",
                  fontWeight: 700 }}>
      ◌ {label}
    </div>
  );
}

/**
 * Cyber-news article view (category=news). Hero image + summary + byline +
 * share row. The Share row pushes pre-formed text including a "Shared from
 * QorTrace" tagline + UTM so socials route traffic back to the radar.
 */
function NewsArticleDetail({ detail, point, accent }) {
  const image   = detail.image || point.image;
  const summary = detail.summary || point.summary;
  const source  = (point.source || detail.source || "").replace(/^news_/, "").toUpperCase();
  return (
    <>
      {image && (
        <img data-testid="tr-news-hero"
             src={image}
             alt={`${point.label} cover`}
             onError={(e) => { e.currentTarget.style.display = "none"; }}
             style={{ width: "100%", borderRadius: 6, marginBottom: 14,
                      maxHeight: 220, objectFit: "cover",
                      border: `1px solid ${accent}33` }} />
      )}
      <div style={{ color: accent, fontFamily: "ui-monospace,Menlo,monospace",
                    letterSpacing: "0.16em", fontSize: 11, fontWeight: 700,
                    marginBottom: 8 }}>
        {source}
        {point.ts && <span style={{ color: "#9aa5b8", marginLeft: 10 }}>
          · {new Date(point.ts).toLocaleString()}
        </span>}
      </div>

      {summary && (
        <p data-testid="tr-news-summary"
           style={{ color: "#dde3ee", fontSize: 15, lineHeight: 1.65,
                    margin: "0 0 14px" }}>
          {summary}
        </p>
      )}

      <ShareRow point={point} detail={detail} accent={accent} />
    </>
  );
}

function ShareRow({ point, detail, accent }) {
  const [copied, setCopied] = useState(false);
  const tagline = "via @QorTrace · Threat Radar";
  const utm = "?utm_source=share&utm_medium=social&utm_campaign=threat-radar";
  const sharedTitle = (detail?.title || point.label || "").slice(0, 220);
  const articleUrl = (point.url || detail?.nvd_url || "").trim();
  // Always also share the Threat Radar deep-link so the click brings the
  // reader back into our globe — this is the "tied to us" hook.
  const radarLink = `https://qortrace.com/threat-radar?inspect=${encodeURIComponent(point.id || "")}${utm.replace("?", "&")}`;
  const composed = `${sharedTitle}\n\n${articleUrl}\n\nShared from QorTrace Threat Radar → ${radarLink}`;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(composed);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (_e) { /* clipboard blocked */ }
  };

  const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${sharedTitle} ${tagline}`)}&url=${encodeURIComponent(radarLink)}`;
  const li = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(radarLink)}`;
  const rd = `https://www.reddit.com/submit?title=${encodeURIComponent(sharedTitle)}&url=${encodeURIComponent(radarLink)}`;

  return (
    <div data-testid="tr-share-row"
         style={{ marginTop: 18, paddingTop: 14,
                  borderTop: "1px solid rgba(255,255,255,0.10)" }}>
      <div style={{ color: accent, fontFamily: "ui-monospace,Menlo,monospace",
                    letterSpacing: "0.18em", fontSize: 11, fontWeight: 700,
                    marginBottom: 10 }}>
        SHARE FROM QORTRACE
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button data-testid="tr-share-copy" onClick={onCopy}
                style={shareBtn(accent, true)}>
          {copied ? <><Check size={14} /> COPIED</> : <><Copy size={14} /> COPY LINK</>}
        </button>
        <a href={tw} target="_blank" rel="noopener noreferrer"
           data-testid="tr-share-twitter"
           style={shareBtn("#1d9bf0")}>
          <Twitter size={14} /> POST ON X
        </a>
        <a href={li} target="_blank" rel="noopener noreferrer"
           data-testid="tr-share-linkedin"
           style={shareBtn("#0a66c2")}>
          <Linkedin size={14} /> LINKEDIN
        </a>
        <a href={rd} target="_blank" rel="noopener noreferrer"
           data-testid="tr-share-reddit"
           style={shareBtn("#ff4500")}>
          <Share2 size={14} /> REDDIT
        </a>
      </div>
      <div style={{ color: "#7a8398", fontSize: 11, marginTop: 8,
                    fontFamily: "ui-monospace,Menlo,monospace" }}>
        Every share carries a Threat-Radar deep-link & UTM so readers bounce back here.
      </div>
    </div>
  );
}

function shareBtn(color, primary = false) {
  return {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "9px 12px", borderRadius: 4, cursor: "pointer",
    background: primary ? `${color}1a` : "transparent",
    border: `1px solid ${color}55`, color,
    fontFamily: "ui-monospace,Menlo,monospace",
    fontSize: 11, letterSpacing: "0.12em", fontWeight: 700,
    textDecoration: "none", whiteSpace: "nowrap",
  };
}

/**
 * Quantum facility detail — logo + bio + Google-News-backed PR feed.
 */
function QuantumFacilityDetail({ detail, accent, point }) {
  const profile  = detail.profile || {};
  const listings = profile.listings || [];
  const facType  = detail.type === "defense" ? "DEFENSE" : "COMPUTE";
  const facColor = detail.type === "defense" ? "#00E5FF" : "#9B7CFF";

  // Live Yahoo quote pull — only for the listings on this profile
  const [quotes, setQuotes] = useState({});
  useEffect(() => {
    const symbols = listings.map((l) => l.yahoo).filter(Boolean);
    if (!symbols.length) return;
    let cancelled = false;
    axios.get(`${API}/api/public/threat-radar/quotes`,
              { params: { symbols: symbols.join(",") } })
         .then((r) => { if (!cancelled) setQuotes(r.data?.quotes || {}); })
         .catch(() => {});
    return () => { cancelled = true; };
    // listings is derived from detail; recompute when its identity changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.id]);

  return (
    <>
      <div style={{ display: "inline-block", marginBottom: 10,
                    background: `${facColor}15`,
                    border: `1px solid ${facColor}66`,
                    color: facColor, padding: "4px 10px",
                    fontFamily: "ui-monospace,Menlo,monospace",
                    fontSize: 10, fontWeight: 800, letterSpacing: "0.18em",
                    borderRadius: 3 }}>
        QUANTUM · {facType}
      </div>
      {profile.logo && (
        <div style={{ background: "rgba(255,255,255,0.04)",
                      border: `1px solid ${accent}33`,
                      borderRadius: 6, padding: "16px 20px",
                      marginBottom: 14, display: "flex",
                      alignItems: "center", justifyContent: "center",
                      minHeight: 90, position: "relative" }}>
          <img data-testid="tr-quantum-logo"
               src={profile.logo}
               alt={`${detail.vendor} logo`}
               onError={(e) => { e.currentTarget.style.display = "none"; }}
               style={{ maxWidth: "100%", maxHeight: 60, objectFit: "contain",
                        filter: "brightness(1.05)" }} />
          {listings.length > 0 && (
            <div data-testid="tr-quantum-ticker"
                 style={{ position: "absolute", top: 8, right: 8,
                          background: "#1FDD8322",
                          border: "1px solid #1FDD83",
                          color: "#1FDD83",
                          padding: "3px 8px", borderRadius: 4,
                          fontFamily: "ui-monospace,Menlo,monospace",
                          fontSize: 11, fontWeight: 800,
                          letterSpacing: "0.1em" }}>
              {listings.length === 1 ? "PUBLIC" : `LISTED · ${listings.length}×`}
            </div>
          )}
        </div>
      )}

      {/* Listings — one row per exchange with a live Yahoo quote chip */}
      {listings.length > 0 && (
        <div data-testid="tr-quantum-listings"
             style={{ marginBottom: 16,
                      display: "flex", flexDirection: "column", gap: 6 }}>
          {listings.map((l, i) => {
            const q = quotes[l.yahoo];
            const up = q && q.change_pct >= 0;
            const arrow = q ? (up ? "▲" : "▼") : "";
            const qcolor = q ? (up ? "#1FDD83" : "#FF6E6E") : "#9aa5b8";
            return (
              <div key={i}
                   data-testid={`tr-listing-${i}`}
                   style={{ display: "flex", alignItems: "center", gap: 10,
                            padding: "8px 12px",
                            background: "rgba(255,255,255,0.03)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 4 }}>
                <span style={{ color: "#9aa5b8",
                               fontFamily: "ui-monospace,Menlo,monospace",
                               letterSpacing: "0.14em",
                               fontSize: 10, fontWeight: 700, minWidth: 110 }}>
                  {l.exchange}
                </span>
                <span style={{ color: "#fff", fontWeight: 700,
                               fontFamily: "ui-monospace,Menlo,monospace",
                               fontSize: 14 }}>
                  {l.ticker}
                </span>
                {q ? (
                  <span style={{ marginLeft: "auto",
                                 fontFamily: "ui-monospace,Menlo,monospace",
                                 fontSize: 13, fontWeight: 700 }}>
                    <span style={{ color: "#fff" }}>
                      {q.currency === "USD" ? "$" :
                       q.currency === "CAD" ? "C$" :
                       q.currency === "HKD" ? "HK$" :
                       q.currency === "EUR" ? "€" :
                       q.currency === "GBP" ? "£" : ""}
                      {q.price.toFixed(q.price < 10 ? 2 : 2)}
                    </span>
                    <span style={{ color: qcolor, marginLeft: 8, fontSize: 12 }}>
                      {arrow} {Math.abs(q.change_pct).toFixed(2)}%
                    </span>
                  </span>
                ) : (
                  <span style={{ marginLeft: "auto", color: "#64748b",
                                 fontSize: 11,
                                 fontFamily: "ui-monospace,Menlo,monospace" }}>
                    ◌ live quote…
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Quick spec grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                    gap: 8, marginBottom: 16 }}>
        <SpecCell label="System" value={detail.system} accent={accent} />
        <SpecCell label="Qubits" value={detail.qubits != null && detail.qubits > 0
                                          ? String(detail.qubits) : "—"}
                   accent={accent} mono />
        <SpecCell label="Approach" value={profile.approach} accent={accent} />
        <SpecCell label="HQ"       value={profile.hq}       accent={accent} />
        <SpecCell label="Founded"  value={profile.founded}  accent={accent} />
        {listings.length === 0 && (
          <SpecCell label="Status" value="Private" accent={accent} />
        )}
        {listings.length > 0 && (
          <SpecCell label="Status"
                     value={`Public · ${listings.length} listing${listings.length > 1 ? "s" : ""}`}
                     accent="#1FDD83" />
        )}
      </div>

      {/* Bio */}
      {profile.bio && (
        <Section title="Company Brief" accent={accent}>
          <p data-testid="tr-quantum-bio"
             style={{ color: "#dde3ee", fontSize: 15, lineHeight: 1.65, margin: 0 }}>
            {profile.bio}
          </p>
        </Section>
      )}

      {/* Latest news/PRs */}
      {Array.isArray(detail.news) && detail.news.length > 0 && (
        <Section title={`Latest News & PRs · ${detail.vendor}`} accent={accent}>
          <div data-testid="tr-quantum-news">
            {detail.news.slice(0, 8).map((n, i) => (
              <a key={i} href={n.url} target="_blank" rel="noopener noreferrer"
                 style={{ display: "block", textDecoration: "none",
                          borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none",
                          padding: "10px 0", color: "inherit" }}>
                <div style={{ color: "#fff", fontSize: 14, lineHeight: 1.45,
                              marginBottom: 4, fontWeight: 500 }}>
                  {n.title}
                </div>
                <div style={{ color: "#9aa5b8", fontSize: 11,
                              fontFamily: "ui-monospace,Menlo,monospace",
                              letterSpacing: "0.08em",
                              display: "flex", alignItems: "center", gap: 8 }}>
                  <span>{n.source}</span>
                  {n.published && <><span style={{ opacity: 0.4 }}>·</span>
                                    <span>{n.published.split(" ").slice(0, 4).join(" ")}</span></>}
                  <ExternalLink size={11} style={{ marginLeft: "auto", opacity: 0.6 }} />
                </div>
              </a>
            ))}
          </div>
        </Section>
      )}

      {profile.press_url && (
        <a href={profile.press_url} target="_blank" rel="noopener noreferrer"
           data-testid="tr-quantum-pressroom"
           style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6,
                    color: accent, textDecoration: "none",
                    fontFamily: "ui-monospace,Menlo,monospace", fontSize: 12,
                    letterSpacing: "0.1em", fontWeight: 700 }}>
          ▾ FULL PRESS ROOM <ExternalLink size={13} />
        </a>
      )}
    </>
  );
}

function SpecCell({ label, value, accent, mono = false }) {
  if (!value) return <div />;
  return (
    <div style={{ background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  padding: "10px 12px", borderRadius: 4 }}>
      <div style={{ color: accent, fontFamily: "ui-monospace,Menlo,monospace",
                    letterSpacing: "0.18em", fontSize: 10, fontWeight: 700,
                    marginBottom: 4 }}>
        {label.toUpperCase()}
      </div>
      <div style={{ color: "#fff", fontSize: 14, fontWeight: 500,
                    fontFamily: mono ? "ui-monospace,Menlo,monospace" : "inherit" }}>
        {value}
      </div>
    </div>
  );
}

/**
 * Footer for the news drawer: Copy Citation (clipboard) + Generate Audit
 * Ticket (modal → POST /audit-intent). Turns the radar from "informational"
 * into "operational" — analysts can either share a citation upstream or
 * queue an internal audit task in one click.
 */
function DrawerActions({ point, detail, accent }) {
  const [copied,  setCopied]  = useState(false);
  const [showModal, setShowModal] = useState(false);

  const onCopy = async () => {
    const cvss = detail?.cvss_v3 || detail?.cvss_v2;
    const lines = [
      `# ${detail?.cve_id || detail?.ghsa_id || point.label}`,
      detail?.title && detail.title !== (detail.cve_id || detail.ghsa_id)
        ? detail.title : null,
      cvss ? `Severity: ${cvss.base_severity || ""} ${cvss.base_score ?? ""} (CVSS v${cvss.version || "3"})` : null,
      cvss?.vector ? `Vector: ${cvss.vector}` : null,
      detail?.description ? `\n${detail.description}` : null,
      "",
      detail?.nvd_url    ? `Source: ${detail.nvd_url}`            : null,
      point.url && point.url !== detail?.nvd_url ? `Source: ${point.url}` : null,
      detail?.published  ? `Published: ${detail.published}`       : null,
      `\nCited via QorTrace Threat Radar — https://qortrace.com/threat-radar?inspect=${encodeURIComponent(point.id || "")}`,
    ].filter(Boolean);
    const txt = lines.join("\n");
    try {
      await navigator.clipboard.writeText(txt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (_e) { /* clipboard blocked — silent */ }
  };

  return (
    <>
      <div style={{ marginTop: 22, paddingTop: 14, display: "flex", gap: 8,
                    borderTop: "1px solid rgba(255,255,255,0.10)" }}>
        <button data-testid="tr-action-copy-citation" onClick={onCopy}
                style={{ flex: 1, background: "rgba(0,229,255,0.08)",
                         border: `1px solid ${accent}55`, color: "#fff",
                         padding: "10px 12px", borderRadius: 4, cursor: "pointer",
                         fontFamily: "ui-monospace,Menlo,monospace",
                         fontSize: 11, letterSpacing: "0.1em", fontWeight: 700,
                         display: "inline-flex", alignItems: "center",
                         gap: 6, justifyContent: "center" }}>
          {copied ? <><Check size={12} /> COPIED</> : <><Copy size={12} /> COPY CITATION</>}
        </button>
        <button data-testid="tr-action-audit-ticket" onClick={() => setShowModal(true)}
                style={{ flex: 1, background: accent, color: "#04090e",
                         border: "none", padding: "10px 12px", borderRadius: 4,
                         cursor: "pointer", fontFamily: "ui-monospace,Menlo,monospace",
                         fontSize: 11, letterSpacing: "0.1em", fontWeight: 800,
                         display: "inline-flex", alignItems: "center",
                         gap: 6, justifyContent: "center" }}>
          <Ticket size={12} /> AUDIT TICKET
        </button>
      </div>
      {showModal && (
        <AuditIntentModal point={point} detail={detail}
                          onClose={() => setShowModal(false)} accent={accent} />
      )}
    </>
  );
}

function AuditIntentModal({ point, detail, accent, onClose }) {
  const [email, setEmail] = useState("");
  const [org,   setOrg]   = useState("");
  const [note,  setNote]  = useState("");
  const [busy,  setBusy]  = useState(false);
  const [done,  setDone]  = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !org) return;
    setBusy(true); setError(null);
    const cvss = detail?.cvss_v3 || detail?.cvss_v2;
    try {
      await axios.post(`${API}/api/public/threat-radar/audit-intent`, {
        advisory_id:    point.id || detail?.cve_id || detail?.ghsa_id || "unknown",
        advisory_url:   detail?.nvd_url || point.url || null,
        advisory_title: detail?.title || point.label || null,
        severity:       cvss?.base_severity || (point.category || "").toUpperCase() || null,
        cvss_score:     cvss?.base_score ?? null,
        email,
        org,
        note: note || null,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="tr-audit-modal"
         onClick={onClose}
         style={{ position: "fixed", inset: 0, zIndex: 100,
                  background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: "#04090e", border: `1px solid ${accent}`,
                    padding: 24, borderRadius: 6, maxWidth: 460, width: "100%",
                    maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div style={{ color: accent, fontFamily: "ui-monospace,Menlo,monospace",
                          letterSpacing: "0.16em", fontSize: 10, fontWeight: 700 }}>
              QORTRACE · AUDIT TICKET
            </div>
            <div style={{ color: "#fff", fontSize: 16, fontWeight: 700,
                          marginTop: 4, lineHeight: 1.3 }}>
              {detail?.cve_id || detail?.ghsa_id || point.label}
            </div>
          </div>
          <button onClick={onClose} data-testid="tr-audit-modal-close"
                  style={{ background: "transparent", border: "none",
                           color: "#9aa5b8", cursor: "pointer" }}>
            <X size={18} />
          </button>
        </div>

        {!done ? (
          <form onSubmit={submit}>
            <p style={{ color: "#cdd3e1", fontSize: 12, lineHeight: 1.5,
                        marginBottom: 14 }}>
              Queue an internal review of this advisory for your stack. Our
              audit team responds within 1 business day with exposure scope
              + a remediation path.
            </p>
            <ModalField label="Work email" value={email} onChange={setEmail}
                        type="email" required testid="tr-audit-modal-email" />
            <ModalField label="Organisation" value={org} onChange={setOrg}
                        required testid="tr-audit-modal-org" />
            <ModalField label="Optional note" value={note} onChange={setNote}
                        textarea testid="tr-audit-modal-note" />
            {error && (
              <div style={{ color: "#FF8000", fontSize: 11, marginBottom: 8,
                            fontFamily: "ui-monospace,Menlo,monospace" }}>
                {String(error).slice(0, 240)}
              </div>
            )}
            <button type="submit" disabled={busy || !email || !org}
                    data-testid="tr-audit-modal-submit"
                    style={{ width: "100%", marginTop: 6, background: accent,
                             color: "#04090e", border: "none",
                             padding: "12px 16px", borderRadius: 4,
                             fontFamily: "ui-monospace,Menlo,monospace",
                             letterSpacing: "0.14em", fontSize: 12,
                             fontWeight: 800, cursor: busy ? "wait" : "pointer",
                             opacity: busy || !email || !org ? 0.6 : 1 }}>
              {busy ? "QUEUEING…" : "SUBMIT AUDIT TICKET"}
            </button>
          </form>
        ) : (
          <div data-testid="tr-audit-modal-done">
            <div style={{ color: "#1FDD83",
                          fontFamily: "ui-monospace,Menlo,monospace",
                          letterSpacing: "0.16em", fontSize: 11, fontWeight: 700 }}>
              ✓ TICKET QUEUED
            </div>
            <p style={{ color: "#cdd3e1", fontSize: 13, lineHeight: 1.5,
                        marginTop: 10 }}>
              Got it. Our audit team will respond to <b>{email}</b> within 1
              business day with exposure scope and a remediation path
              specific to {org}.
            </p>
            <button onClick={onClose}
                    style={{ marginTop: 14, background: "transparent",
                             border: `1px solid ${accent}`, color: accent,
                             padding: "10px 16px", borderRadius: 4,
                             fontFamily: "ui-monospace,Menlo,monospace",
                             letterSpacing: "0.14em", fontSize: 11,
                             fontWeight: 700, cursor: "pointer", width: "100%" }}>
              CLOSE
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ModalField({ label, value, onChange, type = "text", required = false,
                      textarea = false, testid }) {
  const C = textarea ? "textarea" : "input";
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <div style={{ color: "#9aa5b8", fontFamily: "ui-monospace,Menlo,monospace",
                    letterSpacing: "0.14em", fontSize: 9, marginBottom: 6 }}>
        {label.toUpperCase()}{required ? " *" : ""}
      </div>
      <C value={value} onChange={(e) => onChange(e.target.value)}
         type={type} required={required} data-testid={testid}
         rows={textarea ? 3 : undefined}
         style={{ width: "100%", background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.12)", color: "#fff",
                  padding: "10px 12px", borderRadius: 4, fontSize: 13,
                  fontFamily: textarea ? "inherit" : "ui-monospace,Menlo,monospace",
                  resize: textarea ? "vertical" : "none", outline: "none" }} />
    </label>
  );
}

/**
 * NIST-style detail body. Renders sections in the order NVD uses on
 * https://nvd.nist.gov/vuln/detail/{cve}.
 */
function NvdStyleDetail({ detail, accent }) {
  const cvss = detail.cvss_v3 || detail.cvss_v2;
  const sevColor = sevToColor(cvss?.base_severity || detail.severity);
  return (
    <>
      {/* DESCRIPTION */}
      {detail.description && (
        <Section title="Description" accent={accent}>
          <p data-testid="tr-detail-description"
             style={{ color: "#dde3ee", fontSize: 15, lineHeight: 1.65, margin: 0 }}>
            {detail.description}
          </p>
        </Section>
      )}

      {/* SEVERITY */}
      {cvss && (
        <Section title="Severity" accent={accent}>
          <div data-testid="tr-detail-cvss"
               style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{ background: sevColor, color: "#04090e", padding: "5px 12px",
                          borderRadius: 4, fontWeight: 800, fontSize: 15,
                          fontFamily: "ui-monospace,Menlo,monospace" }}>
              {(cvss.base_score ?? "—")} {cvss.base_severity || ""}
            </div>
            <div style={{ color: "#9aa5b8", fontSize: 13, fontWeight: 600,
                          fontFamily: "ui-monospace,Menlo,monospace" }}>
              CVSS v{cvss.version || "3"}
            </div>
          </div>
          {cvss.vector && (
            <div style={{ color: "#cdd3e1", fontSize: 12, wordBreak: "break-all",
                          fontFamily: "ui-monospace,Menlo,monospace", marginBottom: 8,
                          lineHeight: 1.5 }}>
              {cvss.vector}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8,
                        marginTop: 8 }}>
            {cvss.attack_vector    && <Mini k="AV"  v={cvss.attack_vector} />}
            {cvss.attack_complexity && <Mini k="AC" v={cvss.attack_complexity} />}
            {cvss.privileges       && <Mini k="PR"  v={cvss.privileges} />}
            {cvss.user_interaction && <Mini k="UI"  v={cvss.user_interaction} />}
            {cvss.scope            && <Mini k="S"   v={cvss.scope} />}
            {cvss.confidentiality  && <Mini k="C"   v={cvss.confidentiality} />}
            {cvss.integrity        && <Mini k="I"   v={cvss.integrity} />}
            {cvss.availability     && <Mini k="A"   v={cvss.availability} />}
          </div>
          {cvss.source && (
            <div style={{ color: "#7a8398", fontSize: 11, marginTop: 10,
                          fontFamily: "ui-monospace,Menlo,monospace" }}>
              Source: {cvss.source}{cvss.type ? ` · ${cvss.type}` : ""}
            </div>
          )}
        </Section>
      )}

      {/* KEV — CISA required action / due date */}
      {detail.kev_extras && (detail.kev_extras.required_action || detail.kev_extras.due_date) && (
        <Section title="CISA Known Exploited" accent="#FF2A2A">
          {detail.kev_extras.vendor && (
            <Field label="VENDOR / PRODUCT"
                   value={`${detail.kev_extras.vendor || ""} ${detail.kev_extras.product || ""}`.trim()} />
          )}
          {detail.kev_extras.required_action && (
            <Field label="REQUIRED ACTION" value={detail.kev_extras.required_action} />
          )}
          {detail.kev_extras.due_date && (
            <Field label="DUE DATE" value={detail.kev_extras.due_date} mono />
          )}
        </Section>
      )}

      {/* WEAKNESSES (CWE) */}
      {Array.isArray(detail.weaknesses) && detail.weaknesses.length > 0 && (
        <Section title="Weakness Enumeration" accent={accent}>
          <div data-testid="tr-detail-weaknesses"
               style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {dedupe(detail.weaknesses.map((w) => w.cwe)).map((cwe) => (
              <a key={cwe} href={`https://cwe.mitre.org/data/definitions/${cwe.replace("CWE-", "")}.html`}
                 target="_blank" rel="noopener noreferrer"
                 style={{ border: `1px solid ${accent}55`, color: accent, padding: "3px 8px",
                          borderRadius: 4, fontSize: 11, textDecoration: "none",
                          fontFamily: "ui-monospace,Menlo,monospace" }}>
                {cwe}
              </a>
            ))}
          </div>
        </Section>
      )}

      {/* GHSA — affected packages */}
      {detail.ghsa_extras?.packages?.length > 0 && (
        <Section title="Affected Packages" accent={accent}>
          {detail.ghsa_extras.packages.slice(0, 6).map((p, i) => (
            <div key={i} style={{ borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none",
                                  padding: "8px 0" }}>
              <div style={{ color: "#fff", fontSize: 12, fontWeight: 600,
                            fontFamily: "ui-monospace,Menlo,monospace" }}>
                {p.ecosystem}/{p.name}
              </div>
              {p.vulnerable_version_range && (
                <div style={{ color: "#FF8000", fontSize: 10, marginTop: 2,
                              fontFamily: "ui-monospace,Menlo,monospace" }}>
                  Vulnerable: {p.vulnerable_version_range}
                </div>
              )}
              {p.patched_versions && (
                <div style={{ color: "#1FDD83", fontSize: 10, marginTop: 2,
                              fontFamily: "ui-monospace,Menlo,monospace" }}>
                  Patched: {p.patched_versions}
                </div>
              )}
            </div>
          ))}
        </Section>
      )}

      {/* CONFIGURATIONS — vendor / product CPE */}
      {Array.isArray(detail.configurations) && detail.configurations.length > 0 && (
        <Section title="Affected Configurations" accent={accent}>
          {detail.configurations.slice(0, 8).map((c, i) => (
            <div key={i} style={{ borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none",
                                  padding: "6px 0", color: "#cdd3e1", fontSize: 10,
                                  fontFamily: "ui-monospace,Menlo,monospace",
                                  wordBreak: "break-all" }}>
              {prettyCpe(c.cpe)}
              {(c.version_start || c.version_end) && (
                <div style={{ color: "#9aa5b8", marginTop: 2 }}>
                  {c.version_start ? `≥ ${c.version_start}` : ""}
                  {c.version_end ? `  ≤ ${c.version_end}` : ""}
                </div>
              )}
            </div>
          ))}
        </Section>
      )}

      {/* REFERENCES */}
      {Array.isArray(detail.references) && detail.references.length > 0 && (
        <Section title="References to Advisories, Solutions, and Tools" accent={accent}>
          <div data-testid="tr-detail-references">
            {detail.references.slice(0, 12).map((r, i) => (
              <div key={i} style={{ borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none",
                                    padding: "8px 0" }}>
                <a href={r.url} target="_blank" rel="noopener noreferrer"
                   style={{ color: "#cdd3e1", fontSize: 11, textDecoration: "none",
                            wordBreak: "break-all", display: "inline-flex", gap: 4,
                            alignItems: "center" }}>
                  <ExternalLink size={10} style={{ flexShrink: 0, opacity: 0.6 }} />
                  {r.url}
                </a>
                {r.tags?.length > 0 && (
                  <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                    {r.tags.map((t) => (
                      <span key={t} style={{ background: "rgba(255,255,255,0.06)",
                                              color: "#9aa5b8", padding: "1px 6px",
                                              borderRadius: 2, fontSize: 9,
                                              fontFamily: "ui-monospace,Menlo,monospace" }}>
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* METADATA */}
      <Section title="Metadata" accent={accent}>
        {detail.published     && <Field label="PUBLISHED"     value={fmtDate(detail.published)} mono />}
        {detail.last_modified && <Field label="LAST MODIFIED" value={fmtDate(detail.last_modified)} mono />}
        {detail.vuln_status   && <Field label="STATUS"        value={detail.vuln_status} mono />}
        {detail.source_id     && <Field label="SOURCE"        value={detail.source_id} mono />}
        {detail.cve_id        && <Field label="CVE ID"        value={detail.cve_id} mono />}
        {detail.ghsa_id       && <Field label="GHSA ID"       value={detail.ghsa_id} mono />}
      </Section>
    </>
  );
}

function Section({ title, accent, children }) {
  return (
    <div style={{ marginTop: 22, paddingTop: 16,
                  borderTop: "1px solid rgba(255,255,255,0.10)" }}>
      <div style={{ color: accent, fontFamily: "ui-monospace,Menlo,monospace",
                    letterSpacing: "0.18em", fontSize: 11, fontWeight: 700,
                    textTransform: "uppercase", marginBottom: 12 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Mini({ k, v }) {
  return (
    <div style={{ display: "flex", gap: 8, fontSize: 12,
                  fontFamily: "ui-monospace,Menlo,monospace",
                  padding: "3px 0" }}>
      <span style={{ color: "#7a8398", minWidth: 26, fontWeight: 700 }}>{k}</span>
      <span style={{ color: "#dde3ee" }}>{v}</span>
    </div>
  );
}

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleString(); } catch { return String(s); }
}

function dedupe(arr) {
  return [...new Set(arr.filter(Boolean))];
}

function sevToColor(sev) {
  const s = String(sev || "").toUpperCase();
  if (s.startsWith("CRIT")) return "#FF2A2A";
  if (s === "HIGH")         return "#FF8000";
  if (s === "MEDIUM")       return "#FFD60A";
  if (s === "LOW")          return "#1FDD83";
  return "#00E5FF";
}

// CPE 2.3 strings: cpe:2.3:a:vendor:product:version:...
// Pull the human-friendly bits for display.
function prettyCpe(cpe) {
  if (!cpe) return "";
  const parts = cpe.split(":");
  if (parts.length < 6) return cpe;
  const [, , , vendor, product, version] = parts;
  const ver = version && version !== "*" ? ` ${version}` : "";
  return `${vendor} · ${product}${ver}`;
}

function Field({ label, value, mono = false }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ color: "#9aa5b8", fontFamily: "ui-monospace,Menlo,monospace",
                    letterSpacing: "0.16em", fontSize: 11, fontWeight: 700 }}>
        {label}
      </div>
      <div style={{ color: "#fff", marginTop: 4, fontSize: 14, lineHeight: 1.5,
                    fontFamily: mono ? "ui-monospace,Menlo,monospace" : "inherit",
                    wordBreak: "break-word" }}>
        {value}
      </div>
    </div>
  );
}

function SectionGrid({ feed, news }) {
  // Equal-height card target — drives the inner-scroll fallback when a
  // card's content overflows. 480px reads well at 14px+ body copy.
  const CARD_HEIGHT = 480;

  // All four below-fold cards have their own backend feed. Fetched once
  // on mount; not polled because the data refreshes on the server hourly.
  const [hotspots,    setHotspots]    = useState(null);
  const [topVendors,  setTopVendors]  = useState(null);
  const [deadlines,   setDeadlines]   = useState(null);
  const [pulse,       setPulse]       = useState(null);
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/api/public/threat-radar/hotspots?days=14&limit=8`),
      axios.get(`${API}/api/public/threat-radar/top-vendors?days=30&limit=8`),
      axios.get(`${API}/api/public/threat-radar/compliance-deadlines`),
      axios.get(`${API}/api/public/threat-radar/community-pulse`),
    ]).then(([h, v, d, p]) => {
      if (cancelled) return;
      setHotspots(h.data?.hotspots || []);
      setTopVendors(v.data?.vendors || []);
      setDeadlines(d.data?.deadlines || []);
      setPulse(p.data?.pulse || []);
    }).catch(() => { /* keep nulls — cards show their own empty state */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ display: "grid", gap: 24,
                  gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))" }}>

      {/* Inline mini-scan — top-left, anchors the page conversion-funnel */}
      <InlineMiniScanCard height={CARD_HEIGHT} />

      {/* Regional Hotspots */}
      <HotspotsCard rows={hotspots} height={CARD_HEIGHT} />

      {/* NIST PQC Tracker */}
      <BlockCard title="NIST PQC STANDARDS" accent="#9B7CFF" icon={Shield}
                 height={CARD_HEIGHT}>
        <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>
          {(feed?.nist_pqc || []).map((s) => (
            <div key={s.fips} style={{ borderTop: "1px solid rgba(255,255,255,0.08)",
                                        padding: "14px 0", display: "flex",
                                        justifyContent: "space-between", gap: 10 }}>
              <div>
                <div style={{ color: "#fff", fontWeight: 600, fontSize: 15 }}>{s.name}</div>
                <div style={{ color: "#9aa5b8", fontSize: 12, marginTop: 3 }}>
                  {s.fips} · {s.kind}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ color: s.status.startsWith("Standardised") ? "#1FDD83"
                                    : s.status.startsWith("Selected") ? "#9B7CFF"
                                    : "#FFD60A",
                              fontFamily: "ui-monospace,Menlo,monospace", fontSize: 12,
                              letterSpacing: "0.06em", fontWeight: 700 }}>
                  {s.status.toUpperCase()}
                </div>
                <div style={{ color: "#6b7280", fontSize: 11, marginTop: 3 }}>{s.year}</div>
              </div>
            </div>
          ))}
        </div>
      </BlockCard>

      {/* Threat Stats */}
      <BlockCard title="THREAT FEED · LAST 14 DAYS" accent="#FF8000" icon={AlertTriangle}
                 height={CARD_HEIGHT}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {Object.entries(feed?.stats?.by_category || {}).map(([k, v]) => (
            <div key={k} style={{ background: "rgba(255,255,255,0.03)",
                                   padding: "16px 14px", borderRadius: 3 }}>
              <div style={{ fontSize: 32, fontWeight: 700, color: "#fff",
                            fontFamily: "ui-monospace,Menlo,monospace", lineHeight: 1 }}>
                {v}
              </div>
              <div style={{ color: "#9aa5b8", fontSize: 12, marginTop: 8,
                            textTransform: "uppercase", letterSpacing: "0.1em" }}>
                {k}
              </div>
            </div>
          ))}
        </div>
        <div style={{ color: "#9aa5b8", fontSize: 13, marginTop: 18, lineHeight: 1.5 }}>
          Sources: CISA Known Exploited Vulnerabilities catalog, NIST NVD CVE feed,
          GitHub Advisory Database, and curated cybersecurity + Web3 RSS feeds.
          Refreshed hourly.
        </div>
      </BlockCard>

      {/* Most-Targeted Vendors */}
      <TopVendorsCard rows={topVendors} height={CARD_HEIGHT} />

      {/* Quantum Race Leaderboard */}
      <QuantumRaceCard leaderboard={feed?.qubits?.leaderboard}
                       sota={feed?.qubits?.physical} height={CARD_HEIGHT} />

      {/* PQC Compliance Countdown */}
      <ComplianceCountdownCard rows={deadlines} height={CARD_HEIGHT} />

      {/* Community Pulse */}
      <CommunityPulseCard rows={pulse} height={CARD_HEIGHT} />

      {/* Latest Advisories — scrollable so the column matches the others' height */}
      <BlockCard title="LATEST ADVISORIES" accent="#00E5FF" icon={Newspaper}
                 testId="tr-latest-advisories" height={CARD_HEIGHT}>
        <div style={{ flex: 1, overflowY: "auto", paddingRight: 6 }}>
          {news.slice(0, 30).map((n, i) => (
            <a key={i} href={n.url} target="_blank" rel="noopener noreferrer"
               style={{ display: "block", padding: "14px 0",
                        borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,0.08)",
                        textDecoration: "none" }}>
              <div style={{ color: "#9aa5b8", fontFamily: "ui-monospace,Menlo,monospace",
                            fontSize: 11, letterSpacing: "0.14em", marginBottom: 5 }}>
                {(n.source || "").toUpperCase()} · {n.severity?.toUpperCase()}
              </div>
              <div style={{ color: "#fff", fontSize: 14, lineHeight: 1.4,
                            fontWeight: 600 }}>
                {n.title}
              </div>
              {n.summary && (
                <div style={{ color: "#9aa5b8", fontSize: 13, marginTop: 6, lineHeight: 1.5,
                              display: "-webkit-box", WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {n.summary}
                </div>
              )}
            </a>
          ))}
          {!news?.length && (
            <div style={{ color: "#6b7280", fontSize: 13, padding: "10px 0" }}>
              Awaiting first ingest cycle…
            </div>
          )}
        </div>
      </BlockCard>
    </div>
  );
}

function BlockCard({ title, accent, icon: Icon, children, testId, height }) {
  return (
    <div data-testid={testId}
         style={{ background: "rgba(8,12,22,0.55)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderTop: `2px solid ${accent}`,
                  padding: 24, borderRadius: 3,
                  height: height || "auto",
                  display: "flex", flexDirection: "column",
                  overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8,
                    fontFamily: "ui-monospace,Menlo,monospace",
                    color: accent, letterSpacing: "0.14em", fontSize: 12,
                    fontWeight: 700, marginBottom: 14, flexShrink: 0 }}>
        {Icon && <Icon size={14} />} {title}
      </div>
      {children}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Card: Inline mini-scan
// Single-input ad-hoc PQC scan. 1 free anonymous scan per IP per day; once
// the user trips the quota the card flips to "create a free account" CTA
// driven by the existing /api/scan 429 response (which already carries
// signup_url + a human-readable message). Highest-converting placement on
// the whole page.
// ─────────────────────────────────────────────────────────────────────────────
function InlineMiniScanCard({ height }) {
  const [chain, setChain]       = useState("ethereum");
  const [address, setAddress]   = useState("");
  const [busy, setBusy]         = useState(false);
  const [result, setResult]     = useState(null);
  const [gated, setGated]       = useState(null);   // {message, signup_url}
  const [err, setErr]           = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!address.trim() || busy) return;
    setBusy(true); setErr(null); setGated(null); setResult(null);
    try {
      const r = await axios.post(`${API}/api/scan`,
        { chain, address: address.trim() });
      setResult(r.data);
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      if (e2?.response?.status === 429
          && (detail?.code === "scan_rate_limited"
              || detail?.scope === "anon_ip_daily")) {
        setGated({
          message:    detail.message || "Free anonymous scan exhausted.",
          signup_url: detail.signup_url || "/signin",
          used:       detail.used,
          limit:      detail.limit,
        });
      } else {
        setErr(typeof detail === "string" ? detail
              : detail?.message || "Scan failed. Try again.");
      }
    } finally { setBusy(false); }
  };

  const score = result?.scoring?.score;
  const accent = "#00E5FF";

  return (
    <BlockCard title="FREE PQC SCAN · 1 PER DAY" accent={accent} icon={Search}
               testId="tr-mini-scan">
      {!result && !gated && (
        <form onSubmit={submit} data-testid="tr-mini-scan-form"
              style={{ display: "flex", flexDirection: "column", gap: 10,
                       flex: 1 }}>
          <p style={{ color: "#9aa5b8", fontSize: 13, lineHeight: 1.5, margin: 0 }}>
            Paste a wallet or contract address and we'll score its
            cryptographic exposure on a 0-100 scale. Free, no card,
            instant.
          </p>
          <select value={chain} onChange={(e) => setChain(e.target.value)}
                  data-testid="tr-mini-scan-chain"
                  aria-label="Blockchain"
                  style={{ background: "rgba(0,0,0,0.55)", color: "#fff",
                           border: "1px solid rgba(255,255,255,0.18)",
                           padding: "10px 12px", fontSize: 13,
                           borderRadius: 3, fontFamily: "inherit" }}>
            <option value="ethereum">Ethereum (EVM)</option>
            <option value="bitcoin">Bitcoin</option>
            <option value="solana">Solana</option>
            <option value="polygon">Polygon</option>
            <option value="bsc">BNB Smart Chain</option>
            <option value="arbitrum">Arbitrum</option>
            <option value="base">Base</option>
            <option value="optimism">Optimism</option>
            <option value="tron">Tron</option>
          </select>
          <input
            type="text" required
            placeholder="0x… or 1A1zP1eP… or So11…"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            data-testid="tr-mini-scan-address"
            aria-label="Wallet address to scan"
            style={{ background: "rgba(0,0,0,0.55)", color: "#fff",
                     border: "1px solid rgba(255,255,255,0.18)",
                     padding: "10px 12px", fontSize: 13, borderRadius: 3,
                     fontFamily: "ui-monospace,Menlo,monospace" }}
          />
          <button type="submit" disabled={busy}
                  data-testid="tr-mini-scan-submit"
                  style={{ background: busy ? "rgba(0,229,255,0.4)" : accent,
                           color: "#000", border: "none",
                           padding: "11px 14px", fontSize: 13, fontWeight: 700,
                           letterSpacing: "0.06em", borderRadius: 3,
                           cursor: busy ? "wait" : "pointer", marginTop: "auto" }}>
            {busy ? "SCANNING…" : "RUN FREE SCAN"}
          </button>
          {err && (
            <div data-testid="tr-mini-scan-error"
                 style={{ color: "#FF6E6E", fontSize: 12 }}>{err}</div>
          )}
        </form>
      )}
      {gated && (
        <div data-testid="tr-mini-scan-gated"
             style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
          <div style={{ color: "#FF8000", fontFamily: "ui-monospace,Menlo,monospace",
                        fontSize: 11, letterSpacing: "0.14em", fontWeight: 700 }}>
            ◉ FREE SCAN USED · {gated.used}/{gated.limit}
          </div>
          <div style={{ color: "#fff", fontSize: 15, lineHeight: 1.5 }}>
            {gated.message}
          </div>
          <Link to={gated.signup_url}
                data-testid="tr-mini-scan-signup"
                style={{ background: accent, color: "#000",
                         padding: "11px 14px", fontSize: 13, fontWeight: 700,
                         letterSpacing: "0.04em", borderRadius: 3,
                         textDecoration: "none", textAlign: "center",
                         marginTop: "auto" }}>
            Create a free account →
          </Link>
        </div>
      )}
      {result && (
        <div data-testid="tr-mini-scan-result"
             style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
          <div style={{ color: accent, fontFamily: "ui-monospace,Menlo,monospace",
                        fontSize: 11, letterSpacing: "0.14em", fontWeight: 700 }}>
            ◉ SCAN COMPLETE · {result.chain_label}
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <div style={{ fontSize: 56, fontFamily: "ui-monospace,Menlo,monospace",
                          fontWeight: 700, color: scoreColor(score), lineHeight: 1 }}>
              {Math.round(score ?? 0)}
            </div>
            <div style={{ color: "#9aa5b8", fontSize: 13 }}>/ 100 PQC readiness</div>
          </div>
          <div style={{ color: "#cdd3e1", fontSize: 13, lineHeight: 1.5 }}>
            {result?.scoring?.summary || result?.advisor?.summary || "No summary."}
          </div>
          <div style={{ marginTop: "auto", display: "flex", gap: 8 }}>
            <button onClick={() => { setResult(null); setAddress(""); }}
                    style={{ background: "transparent", color: "#fff",
                             border: "1px solid rgba(255,255,255,0.22)",
                             padding: "9px 14px", fontSize: 12, fontWeight: 600,
                             borderRadius: 3, cursor: "pointer" }}>
              New scan
            </button>
            <Link to="/scan" style={{ flex: 1, background: accent, color: "#000",
                                       padding: "9px 14px", fontSize: 12, fontWeight: 700,
                                       letterSpacing: "0.04em", borderRadius: 3,
                                       textDecoration: "none", textAlign: "center" }}>
              Open full scanner →
            </Link>
          </div>
        </div>
      )}
    </BlockCard>
  );
}

function scoreColor(score) {
  if (score == null) return "#9aa5b8";
  if (score >= 80) return "#1FDD83";
  if (score >= 50) return "#FFD60A";
  if (score >= 25) return "#FF8000";
  return "#FF2A2A";
}


// ─────────────────────────────────────────────────────────────────────────────
// Card: Regional Hotspots — Top countries by advisory count, each row
// links to /r/<code> so the user lands on the regional Threat Radar view
// (which already pulls the regional OG card + Tweet-this-view button).
// ─────────────────────────────────────────────────────────────────────────────
function HotspotsCard({ rows, height }) {
  return (
    <BlockCard title="REGIONAL HOTSPOTS · 14d" accent="#FF2A2A" icon={MapPin}
               testId="tr-hotspots" height={height}>
      <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>
        {!rows && (
          <div style={{ color: "#6b7280", fontSize: 13, padding: "10px 0" }}>
            Loading regional breakdown…
          </div>
        )}
        {rows && rows.length === 0 && (
          <div style={{ color: "#6b7280", fontSize: 13, padding: "10px 0" }}>
            Not enough geo-tagged advisories yet.
          </div>
        )}
        {(rows || []).map((r, i) => (
          <Link key={r.code} to={`/r/${r.code.toLowerCase()}`}
                data-testid={`tr-hotspot-${r.code}`}
                style={{ display: "flex", alignItems: "center", gap: 12,
                         padding: "12px 0", textDecoration: "none",
                         borderTop: i === 0 ? "none"
                                            : "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ background: "rgba(255,42,42,0.18)", color: "#FF2A2A",
                          fontFamily: "ui-monospace,Menlo,monospace",
                          fontWeight: 700, fontSize: 14, padding: "6px 10px",
                          borderRadius: 3, letterSpacing: "0.04em",
                          minWidth: 48, textAlign: "center" }}>
              {r.code}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>
                {r.total} advisories
              </div>
              <div style={{ color: "#9aa5b8", fontSize: 11, marginTop: 3,
                            fontFamily: "ui-monospace,Menlo,monospace",
                            letterSpacing: "0.04em" }}>
                {r.kev ? <>KEV {r.kev} · </> : null}
                CVE {r.cve} · GHSA {r.vendor} · NEWS {r.news}
              </div>
            </div>
            <ArrowRight size={14} style={{ color: "#9aa5b8" }} />
          </Link>
        ))}
      </div>
    </BlockCard>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Card: Most-Targeted Vendors
// ─────────────────────────────────────────────────────────────────────────────
function TopVendorsCard({ rows, height }) {
  return (
    <BlockCard title="MOST-TARGETED VENDORS · 30d" accent="#FFD60A"
               icon={Building2} testId="tr-top-vendors" height={height}>
      <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}
           tabIndex={0}
           role="region"
           aria-label="Most-targeted vendors leaderboard">
        {!rows && (
          <div style={{ color: "#6b7280", fontSize: 13, padding: "10px 0" }}>
            Loading vendor breakdown…
          </div>
        )}
        {(rows || []).map((r, i) => (
          <div key={r.vendor}
               data-testid={`tr-vendor-${r.vendor}`}
               style={{ display: "flex", alignItems: "center", gap: 12,
                        padding: "12px 0",
                        borderTop: i === 0 ? "none"
                                           : "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "#fff", fontSize: 14, fontWeight: 600,
                            textTransform: "capitalize" }}>
                {r.vendor.replace(/[-_]/g, " ")}
              </div>
              <div style={{ color: "#9aa5b8", fontSize: 11, marginTop: 3,
                            fontFamily: "ui-monospace,Menlo,monospace",
                            letterSpacing: "0.04em" }}>
                {r.kev ? <span style={{ color: "#FF2A2A" }}>KEV {r.kev} · </span> : null}
                CVE {r.cve} · GHSA {r.vendor_advisories}
              </div>
            </div>
            <div style={{ color: "#FFD60A", fontFamily: "ui-monospace,Menlo,monospace",
                          fontWeight: 700, fontSize: 18 }}>
              {r.total}
            </div>
          </div>
        ))}
      </div>
    </BlockCard>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Card: Quantum Race Leaderboard — top vendors by physical qubit count.
// Sourced from `feed.qubits.leaderboard` so no extra fetch.
// ─────────────────────────────────────────────────────────────────────────────
function QuantumRaceCard({ leaderboard, sota, height }) {
  const max = Math.max(1, ...(leaderboard || []).map((x) => x.qubits || 0));
  return (
    <BlockCard title="QUANTUM RACE · LEADERBOARD" accent="#9B7CFF" icon={Atom}
               testId="tr-quantum-race" height={height}>
      <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}
           tabIndex={0}
           role="region"
           aria-label="Quantum race leaderboard">
        {!leaderboard && (
          <div style={{ color: "#6b7280", fontSize: 13, padding: "10px 0" }}>
            Loading leaderboard…
          </div>
        )}
        {(leaderboard || []).map((row, i) => {
          const pct = (100 * (row.qubits || 0)) / max;
          const isSota = sota && sota.vendor === row.vendor
                              && sota.system === row.system;
          return (
            <div key={`${row.vendor}-${row.system}`}
                 style={{ padding: "12px 0",
                          borderTop: i === 0 ? "none"
                                             : "1px solid rgba(255,255,255,0.08)" }}>
              <div style={{ display: "flex", alignItems: "baseline",
                            justifyContent: "space-between", gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>
                    {row.vendor}
                    {isSota && (
                      <span style={{ color: "#9B7CFF", fontSize: 10,
                                     fontFamily: "ui-monospace,Menlo,monospace",
                                     letterSpacing: "0.14em", fontWeight: 700,
                                     marginLeft: 8 }}>
                        ◉ SOTA
                      </span>
                    )}
                  </div>
                  <div style={{ color: "#9aa5b8", fontSize: 11, marginTop: 2 }}>
                    {row.system}
                  </div>
                </div>
                <div style={{ color: "#9B7CFF",
                              fontFamily: "ui-monospace,Menlo,monospace",
                              fontWeight: 700, fontSize: 16 }}>
                  {Number(row.qubits || 0).toLocaleString()}<span style={{
                    fontSize: 11, color: "#9aa5b8", marginLeft: 4 }}>q</span>
                </div>
              </div>
              <div style={{ background: "rgba(155,124,255,0.12)",
                            height: 4, marginTop: 8, borderRadius: 2,
                            overflow: "hidden" }}>
                <div style={{ background: "#9B7CFF", height: "100%",
                              width: `${pct}%`, transition: "width 0.6s ease" }} />
              </div>
            </div>
          );
        })}
      </div>
    </BlockCard>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Card: PQC Compliance Countdown — regulatory cliff dates
// ─────────────────────────────────────────────────────────────────────────────
function ComplianceCountdownCard({ rows, height }) {
  const colorFor = (s) => s === "passed" ? "#6b7280"
                       : s === "imminent" ? "#FF2A2A"
                       : s === "near"     ? "#FF8000"
                       : "#9B7CFF";
  return (
    <BlockCard title="PQC COMPLIANCE COUNTDOWN" accent="#FF8000" icon={Calendar}
               testId="tr-compliance" height={height}>
      <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>
        {!rows && (
          <div style={{ color: "#6b7280", fontSize: 13, padding: "10px 0" }}>
            Loading deadlines…
          </div>
        )}
        {(rows || []).map((r, i) => (
          <a key={r.name} href={r.source_url} target="_blank" rel="noopener noreferrer"
             data-testid={`tr-deadline-${i}`}
             style={{ display: "block", padding: "12px 0",
                      textDecoration: "none",
                      borderTop: i === 0 ? "none"
                                         : "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ display: "flex", alignItems: "baseline",
                          justifyContent: "space-between", gap: 10 }}>
              <div style={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>
                {r.name}
              </div>
              <div style={{ color: colorFor(r.status),
                            fontFamily: "ui-monospace,Menlo,monospace",
                            fontWeight: 700, fontSize: 13, whiteSpace: "nowrap" }}>
                {r.days_left < 0 ? `${Math.abs(r.days_left)}d ago`
                                 : `${r.days_left}d`}
              </div>
            </div>
            <div style={{ color: "#9aa5b8", fontSize: 12, marginTop: 4,
                          fontFamily: "ui-monospace,Menlo,monospace",
                          letterSpacing: "0.06em" }}>
              {r.target_date} · {r.jurisdiction}
            </div>
            <div style={{ color: "#cdd3e1", fontSize: 12, marginTop: 6,
                          lineHeight: 1.5,
                          display: "-webkit-box", WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {r.summary}
            </div>
          </a>
        ))}
      </div>
    </BlockCard>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Card: Community Pulse — curated public mentions about Q-Day / PQC
// ─────────────────────────────────────────────────────────────────────────────
function CommunityPulseCard({ rows, height }) {
  return (
    <BlockCard title="COMMUNITY PULSE · CURATED" accent="#1FDD83"
               icon={MessageCircle} testId="tr-community-pulse" height={height}>
      <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>
        {!rows && (
          <div style={{ color: "#6b7280", fontSize: 13, padding: "10px 0" }}>
            Loading pulse…
          </div>
        )}
        {(rows || []).map((p, i) => (
          <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
             style={{ display: "block", padding: "14px 0",
                      textDecoration: "none",
                      borderTop: i === 0 ? "none"
                                         : "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8,
                          color: "#1FDD83",
                          fontFamily: "ui-monospace,Menlo,monospace",
                          fontSize: 11, letterSpacing: "0.06em",
                          fontWeight: 700, marginBottom: 6 }}>
              <Twitter size={12} /> {p.handle}
            </div>
            <div style={{ color: "#fff", fontSize: 14, lineHeight: 1.5 }}>
              {p.text}
            </div>
          </a>
        ))}
      </div>
    </BlockCard>
  );
}

function BriefingSignup() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr]   = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API}/api/public/threat-briefing/subscribe`, {
        email: email.trim().toLowerCase(),
        source: "threat-radar-hero",
      });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not subscribe. Try again.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid="tr-briefing-done"
           style={{ marginTop: 16, fontFamily: "ui-monospace,Menlo,monospace",
                    color: "#1FDD83", fontSize: 11, letterSpacing: "0.14em",
                    fontWeight: 700 }}>
        ✓ SUBSCRIBED · WEEKLY BRIEFING WILL ARRIVE FRIDAYS
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="tr-briefing-form"
          style={{ marginTop: 4, display: "flex", flexDirection: "column",
                   alignItems: "stretch", gap: 4, width: "100%" }}>
      <div style={{ fontFamily: "ui-monospace,Menlo,monospace", fontSize: 13,
                    letterSpacing: "0.18em", color: "#cdd3e1", marginBottom: 6,
                    fontWeight: 700, textAlign: "center" }}>
        WEEKLY THREAT BRIEFING · FREE
      </div>
      <div style={{ display: "flex", gap: 0,
                    border: "1px solid rgba(255,255,255,0.18)",
                    background: "rgba(0,0,0,0.55)",
                    borderRadius: 3, overflow: "hidden" }}>
        <input
          type="email"
          required
          placeholder="security@yourcompany.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="tr-briefing-email"
          style={{ background: "transparent", border: "none", outline: "none",
                   color: "#fff", padding: "8px 12px", fontSize: 12,
                   flex: 1, minWidth: 0, fontFamily: "inherit" }}
        />
        <button type="submit" disabled={busy}
                data-testid="tr-briefing-submit"
                style={{ background: "#00E5FF", color: "#000", border: "none",
                         padding: "0 14px", cursor: busy ? "wait" : "pointer",
                         fontWeight: 700, fontSize: 11, letterSpacing: "0.1em",
                         fontFamily: "ui-monospace,Menlo,monospace" }}>
          {busy ? "…" : "SUBSCRIBE"}
        </button>
      </div>
      {err && (
        <div style={{ color: "#FF6E6E", fontSize: 11, marginTop: 4 }}>{err}</div>
      )}
    </form>
  );
}


function SEOHead() {
  // SPA — inject document head meta directly. Replaces any prior radar tags.
  // When loaded via /r/<code> the URL carries `?country=DE`; we swap in the
  // regional OG image + headline so the preview reflects the share URL.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = (params.get("country") || "").toUpperCase().trim();
    const isRegional = /^[A-Z]{2}$/.test(code);

    const ogImageUrl = isRegional
      ? `${API}/api/public/og/r/${code.toLowerCase()}.png`
      : `${API}/api/public/threat-radar/og.png`;
    const ogVideoUrl = isRegional
      ? `${API}/api/public/og/r/${code.toLowerCase()}.mp4`
      : `${API}/api/public/threat-radar/og.mp4`;
    const ogTitle = isRegional
      ? `Threat Radar · ${code} — Live Quantum & Web3 Security Intelligence | QorTrace`
      : "Threat Radar — Live Quantum & Web3 Security Intelligence | QorTrace";
    const ogDesc = isRegional
      ? `Live cryptographic exposure for ${code}. Regional CVE/KEV pulses, quantum facility tracker, and the Q-Day countdown — updated continuously by QorTrace.`
      : "Live threat map. Q-Day countdown. Quantum facility tracker. Watch the cryptographic horizon move in real time.";
    const ogUrl = isRegional
      ? `https://www.qortrace.com/r/${code.toLowerCase()}`
      : "https://www.qortrace.com/threat-radar";

    const meta = [
      ["description", "Live map of post-quantum and Web3 security threats. CVE/KEV pulses, quantum-computing milestones, NIST PQC standards, and the Q-Day countdown — updated continuously by QorTrace."],
      ["og:title", ogTitle],
      ["og:description", ogDesc],
      ["og:type", "website"],
      ["og:url", ogUrl],
      ["og:image", ogImageUrl],
      ["og:image:width", "1200"],
      ["og:image:height", "630"],
      ["og:video", ogVideoUrl],
      ["og:video:secure_url", ogVideoUrl],
      ["og:video:type", "video/mp4"],
      ["og:video:width", "1200"],
      ["og:video:height", "676"],
      ["twitter:card", "summary_large_image"],
      ["twitter:title", ogTitle],
      ["twitter:description", ogDesc],
      ["twitter:image", ogImageUrl],
    ];
    meta.forEach(([k, v]) => {
      const isOg = k.startsWith("og:") || k.startsWith("twitter:");
      const sel = isOg ? `meta[property="${k}"]` : `meta[name="${k}"]`;
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        if (isOg) el.setAttribute("property", k); else el.setAttribute("name", k);
        document.head.appendChild(el);
      }
      el.setAttribute("content", v);
    });
  }, []);
  return null;
}


// Great-circle distance in km between two lat/lon points (haversine).
// Used by the visitor-flyover region detector — accurate enough at the
// scale of cities/regions, far cheaper than Vincenty.
function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
          + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2))
          * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

// Backend news category → frontend filter chip key.
// `vendor` is the backend's GHSA bucket; the chip is named "ghsa".
const CATEGORY_TO_CHIP = {
  cve:    "cve",
  kev:    "kev",
  vendor: "ghsa",
  news:   "news",
};



// "Tweet this view" — one-click X share that prefills with a regional
// headline + `qortrace.com/r/<code>` so social previews land on the
// dynamic country OG card. Country priority:
//   1. `?country=<iso2>` from the URL (set by `/r/<code>` redirect)
//   2. `flyTo.country` from the visitor-geo lookup
//   3. Fallback to the generic /threat-radar share
const COUNTRY_NAMES_FE = {
  AE: "United Arab Emirates", AR: "Argentina", AT: "Austria", AU: "Australia",
  BE: "Belgium", BG: "Bulgaria", BR: "Brazil", CA: "Canada", CH: "Switzerland",
  CL: "Chile", CN: "China", CO: "Colombia", CY: "Cyprus", CZ: "Czechia",
  DE: "Germany", DK: "Denmark", EE: "Estonia", EG: "Egypt", ES: "Spain",
  FI: "Finland", FR: "France", GB: "the United Kingdom", GH: "Ghana",
  GR: "Greece", HK: "Hong Kong", HR: "Croatia", HU: "Hungary", ID: "Indonesia",
  IE: "Ireland", IL: "Israel", IN: "India", IR: "Iran", IS: "Iceland",
  IT: "Italy", JP: "Japan", KE: "Kenya", KP: "North Korea", KR: "South Korea",
  KW: "Kuwait", LU: "Luxembourg", LV: "Latvia", MA: "Morocco", MT: "Malta",
  MX: "Mexico", MY: "Malaysia", NG: "Nigeria", NL: "the Netherlands",
  NO: "Norway", NZ: "New Zealand", PE: "Peru", PH: "the Philippines",
  PK: "Pakistan", PL: "Poland", PT: "Portugal", QA: "Qatar", RO: "Romania",
  RS: "Serbia", RU: "Russia", SA: "Saudi Arabia", SE: "Sweden", SG: "Singapore",
  SI: "Slovenia", SK: "Slovakia", TH: "Thailand", TR: "Türkiye", TW: "Taiwan",
  UA: "Ukraine", US: "the United States", VN: "Vietnam", ZA: "South Africa",
};

function TweetThisView({ country }) {
  // URL country wins — that's the explicit /r/<code> path. flyTo.country
  // is the IP-geolocated fallback when no override is present.
  const params = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search) : null;
  const urlCode = (params?.get("country") || "").toUpperCase().trim();
  const detectedCode = (country || "").toUpperCase().trim();
  const code = (/^[A-Z]{2}$/.test(urlCode) ? urlCode
              : /^[A-Z]{2}$/.test(detectedCode) ? detectedCode : "");
  const name = code ? (COUNTRY_NAMES_FE[code] || code) : "";

  const shareUrl = code
    ? `https://qortrace.com/r/${code.toLowerCase()}`
    : "https://qortrace.com/threat-radar";
  const text = code
    ? `The cryptographic clock is already ticking in ${name}. ` +
      `Watch the live threat surface and the Q-Day countdown — in real time. ` +
      `cc @qortrace`
    : `The cryptographic clock is already ticking. ` +
      `Live map of post-quantum and Web3 threats — Q-Day countdown live. ` +
      `cc @qortrace`;
  const intent = `https://twitter.com/intent/tweet?` +
    `text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;

  return (
    <a
      href={intent}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="tr-tweet-this-view"
      style={{
        background: "transparent",
        color: "#1FDD83",
        border: "1px solid rgba(31,221,131,0.55)",
        padding: "9px 14px",
        borderRadius: 3,
        fontWeight: 600,
        fontSize: 13,
        textDecoration: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        fontFamily: "inherit",
        letterSpacing: "0.02em",
        transition: "background 0.18s ease, color 0.18s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#1FDD83";
        e.currentTarget.style.color = "#000";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.color = "#1FDD83";
      }}
      title={code ? `Tweet this view for ${name}` : "Tweet this view"}
    >
      <Twitter size={13} />
      {code ? `Tweet this view · ${code}` : "Tweet this view"}
    </a>
  );
}
